import { useCallback, useEffect, useState } from "react";
import { MailCheck, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { inputCls } from "./shared";
import { ChangePasswordPanel } from "./ChangePasswordPanel";
import { AdminPinPanel } from "./AdminPinPanel";

const EMPTY = { current_password: "", new_email: "", confirm_email: "" };

function PendingNotice({ pending, onCancel }) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-[#FDF8EC] border border-sand p-3.5" data-testid="email-change-pending">
      <MailCheck className="w-5 h-5 shrink-0 text-gold mt-0.5" strokeWidth={2.2} />
      <div className="text-sm text-navy flex-1">
        <p className="font-bold">Verification pending</p>
        <p className="text-slate-600">We emailed a confirmation link to <span className="font-semibold" data-testid="email-change-pending-email">{pending.new_email}</span>. Your login email stays the same until you open it. Link valid for about {pending.minutes_remaining} more minute{pending.minutes_remaining === 1 ? "" : "s"}.</p>
      </div>
      <button type="button" onClick={onCancel} data-testid="email-change-cancel" aria-label="Cancel pending email change" className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-slate-500 hover:text-navy hover:bg-cream-dark/70 transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function AccountSecurityPanel() {
  const [email, setEmail] = useState("");
  const [pinEnabled, setPinEnabled] = useState(false);
  const [pending, setPending] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const load = useCallback(async () => {
    try {
      const [me, p] = await Promise.all([api.get("/auth/me"), api.get("/auth/change-email/pending")]);
      setEmail(me.data.email || "");
      setPinEnabled(Boolean(me.data.pin_enabled));
      setPending(p.data.pending);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_email.trim().toLowerCase() !== form.confirm_email.trim().toLowerCase()) { toast.error("The two email addresses do not match."); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/change-email/request", form);
      toast.success(r.data.message, { duration: 8000 });
      setForm(EMPTY);
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not start the email change.", { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    try {
      const r = await api.delete("/auth/change-email/pending");
      toast.success(r.data.message);
      setPending(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid gap-5" data-testid="account-security">
      <div className="flex items-center gap-3 rounded-xl bg-cream border border-sand p-3.5">
        <ShieldCheck className="w-5 h-5 shrink-0 text-gold" strokeWidth={2.2} />
        <div className="text-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Current login email</p>
          <p className="font-bold text-navy text-sm sm:text-base [overflow-wrap:anywhere]" data-testid="current-login-email">{email || "…"}</p>
        </div>
      </div>

      <AdminPinPanel pinEnabled={pinEnabled} onChanged={load} />

      <div>
        <p className="text-sm font-bold text-navy mb-1">Change Login Email</p>
        <p className="text-xs text-slate-500 mb-3">Enter your current password and the new address twice. We'll email the new address a confirmation link; nothing changes until you open it. Your leads, settings and records stay attached to this same account.</p>
        {pending && <div className="mb-3"><PendingNotice pending={pending} onCancel={cancel} /></div>}
        <form onSubmit={submit} className="grid gap-3 sm:max-w-md" data-testid="change-email-form">
          <label className="block">
            <span className="block text-xs font-bold text-navy mb-1">Current password</span>
            <PasswordInput id="ce-current" testid="change-email-password-input" className={inputCls} value={form.current_password} onChange={set("current_password")} autoComplete="current-password" />
          </label>
          <label className="block">
            <span className="block text-xs font-bold text-navy mb-1">New login email</span>
            <input type="email" required className={inputCls} value={form.new_email} onChange={set("new_email")} autoComplete="off" data-testid="new-email-input" />
          </label>
          <label className="block">
            <span className="block text-xs font-bold text-navy mb-1">Confirm new login email</span>
            <input type="email" required className={inputCls} value={form.confirm_email} onChange={set("confirm_email")} autoComplete="off" data-testid="confirm-email-input" />
          </label>
          <button type="submit" disabled={loading} data-testid="change-email-button" className="min-h-[46px] rounded-xl bg-navy text-cream font-bold text-sm px-6 hover:bg-navy-light transition-colors inline-flex items-center justify-center gap-2 sm:w-fit disabled:opacity-60">
            <MailCheck className="w-4 h-4" /> {loading ? "Sending…" : "Send Verification Link"}
          </button>
        </form>
      </div>

      <div>
        <p className="text-sm font-bold text-navy mb-1">Change Password</p>
        <p className="text-xs text-slate-500 mb-3">Choose a new admin password. You'll stay signed in on this device; any other signed-in sessions are closed for your security.</p>
        <ChangePasswordPanel />
      </div>
    </div>
  );
}
